import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CreditCard, Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui';
import { claimWebIDByToken, getWebIDByIdPublic } from '../services/webids';

/**
 * @component ClaimWebID
 * @description Página para reclamar una pulsera NFC mediante token de claim
 * Ruta: /claim/:webId?ct=<token>
 */

interface WebIDClaimData {
  id: string;
  status: 'unclaimed' | 'assigned' | 'active' | 'paused' | 'lost';
  emergencyProfile?: {
    name?: string;
    phone?: string;
  };
}

const ClaimWebID: React.FC = () => {
  const { webId } = useParams<{ webId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [webIdData, setWebIdData] = useState<WebIDClaimData | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const claimToken = searchParams.get('ct');

  useEffect(() => {
    if (!webId) {
      setError('WebID no válido');
      setLoading(false);
      return;
    }

    loadWebIDData();
  }, [webId]);

  const loadWebIDData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener información básica del WebID (sin datos sensibles)
      const data = await getWebIDByIdPublic(webId!);
      setWebIdData(data);

      // Si no hay token de claim, mostrar error
      if (!claimToken) {
        setError('Token de activación requerido. Escanea el código QR de tu pulsera.');
        return;
      }

      // Si el WebID ya está reclamado, mostrar mensaje apropiado
      if (data.status !== 'unclaimed') {
        setError('Esta pulsera ya ha sido activada.');
        return;
      }

    } catch (err) {
      console.error('Error loading WebID:', err);
      setError('Error al cargar información de la pulsera. Verifica el código.');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!user) {
      // Redirigir a login con return URL
      navigate(`/auth/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }

    if (!webId || !claimToken) {
      setError('Información de activación incompleta');
      return;
    }

    try {
      setClaiming(true);
      setError(null);

      await claimWebIDByToken(webId, claimToken, user.uid);

      setSuccess(true);

      // Redirigir al onboarding después de 2 segundos
      setTimeout(() => {
        navigate(`/onboarding/${webId}`);
      }, 2000);

    } catch (err) {
      console.error('Error claiming WebID:', err);
      setError(err instanceof Error ? err.message : 'Error al activar la pulsera');
    } finally {
      setClaiming(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando información de la pulsera...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            ¡Pulsera Activada!
          </h1>
          <p className="text-gray-600 mb-6">
            Tu pulsera NFC ha sido activada exitosamente.
            Ahora configuraremos tu perfil de emergencia paso a paso.
          </p>
          <div className="animate-pulse">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Activar Pulsera NFC
            </h1>
            <p className="text-gray-600">
              WebID: <span className="font-mono font-semibold">{webId}</span>
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-700 font-medium">Error de Activación</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* WebID Info */}
          {webIdData && !error && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="h-5 w-5 text-blue-600" />
                <h3 className="font-medium text-blue-900">Información de la Pulsera</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700">Estado:</span>
                  <span className="font-medium text-blue-900">
                    {webIdData.status === 'unclaimed' ? 'Sin activar' : 'Activada'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700">WebID:</span>
                  <span className="font-mono font-medium text-blue-900">{webId}</span>
                </div>
              </div>
            </div>
          )}

          {/* Authentication Required */}
          {!user && !error && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <h3 className="font-medium text-yellow-900">Inicio de Sesión Requerido</h3>
              </div>
              <p className="text-sm text-yellow-700">
                Necesitas iniciar sesión para activar esta pulsera en tu cuenta.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {!user ? (
              <Button
                onClick={handleClaim}
                className="w-full"
                size="lg"
              >
                Iniciar Sesión para Activar
              </Button>
            ) : !error && webIdData?.status === 'unclaimed' ? (
              <Button
                onClick={handleClaim}
                disabled={claiming}
                className="w-full"
                size="lg"
              >
                {claiming ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Activando...
                  </div>
                ) : (
                  'Activar Pulsera'
                )}
              </Button>
            ) : null}

            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full"
            >
              Volver al Inicio
            </Button>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              ¿Problemas con la activación?{' '}
              <a href="/soporte" className="text-primary-600 hover:text-primary-700">
                Contacta soporte
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimWebID;
